import { isFreedom, isSamsung } from "@ca.vmedia.tv/domains";
import { Strings } from "@/locales/types/enums/strings";
import { useNuxtApp } from '#app';
import { type ISettingsLocales, LangCodes, LangApiCodes, AvailabilityLanguages } from "@/common/types/languages"
import { useDeviceSettingsStore } from '@/stores/DeviceSettings'
import { storeToRefs } from 'pinia'
import { useUserSettingsStore } from "@/stores/UserSettings";
import { useUserInfoStore } from "@/stores/UserInfo";

export const useLanguagesUtils = async () => {
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isClient,
    } = storeToRefs(UserInfoStoreModule);

    const { $domains, $i18n } = useNuxtApp();
    const DeviceSettingsStoreModule = useDeviceSettingsStore();
    const UserSettingsStoreModule = useUserSettingsStore();
    const { setupSettings } = DeviceSettingsStoreModule;
    const { getterSettings: deviceSettingsFromApi } = storeToRefs(DeviceSettingsStoreModule);
    const { setSettingsLanguage } = UserSettingsStoreModule;
    const {
        getSettings
    } = storeToRefs(UserSettingsStoreModule);

    try {
        await setupSettings();
    } catch (error) {
        console.error(error);
    }

    const settingsLocales = (): ISettingsLocales[] => {
        const locales: ISettingsLocales[] = [
            {
                code: LangCodes.SYSTEM,
                translatedName: Strings.SETTINGS_LANGUAGES_SYSTEM,
            },
            {
                code: LangCodes.EN,
                translatedName: Strings.SETTINGS_LANGUAGES_EN,
            },
        ];
        if (!isFreedom) {
            locales.push(
                {
                    code: LangCodes.FR,
                    translatedName: Strings.SETTINGS_LANGUAGES_FR,
                }
            );
        }
        return locales;
    };
    const getLangLsCode = (): LangCodes | null => {
        return $domains.lsCacher.get<LangCodes>('settings-language');
    };
    const putLangLsCode = (code: LangCodes): void => {
        $domains.lsCacher.put<LangCodes>('settings-language', code);
    };
    const availabilityLanguages = (): AvailabilityLanguages => {
        const appLang = deviceSettingsFromApi.value?.settings?.find(({ key }): boolean => key === 'app_lang');
        return (appLang?.value as AvailabilityLanguages) ?? AvailabilityLanguages.ALL;
    };
    const switchToSystemLang = async (browserLocale?: string): Promise<void> => {
        if (browserLocale === LangCodes.FR) {
            $i18n.setLocale(LangCodes.FR);
        } else {
            $i18n.setLocale(LangCodes.EN);
        }
    };

    const initLanguage =  (): void => {
        let browserLocale = $i18n.getBrowserLocale();
        const appSetLocale = $i18n.getLocaleCookie();
        
        //TODO: Check it for samsung
        if (isSamsung && browserLocale && !navigator.language.includes(browserLocale)) {
            browserLocale = navigator.language.includes(LangCodes.FR) ? LangCodes.FR : LangCodes.EN;
        }
        
        const langs: AvailabilityLanguages = availabilityLanguages();
        switch (langs) {
            case AvailabilityLanguages.EN:
                $i18n.setLocale(LangCodes.EN);
                putLangLsCode(LangCodes.EN);
                return;
        
            case AvailabilityLanguages.FR:
                $i18n.setLocale(LangCodes.FR);
                putLangLsCode(LangCodes.FR);
                return;
            case AvailabilityLanguages.NONE:
                switchToSystemLang(browserLocale);
                return;
            default:
                break;
        }

        const code = getLangLsCode();
        if (!code) {
            if (browserLocale !== appSetLocale && !!appSetLocale) {
                $i18n.setLocale(appSetLocale);
                putLangLsCode(appSetLocale as LangCodes);
            } else {
                putLangLsCode(LangCodes.SYSTEM);
            }
        } else if (code === LangCodes.SYSTEM) {
            switchToSystemLang(browserLocale);
            putLangLsCode(LangCodes.SYSTEM);
        } else {
            $i18n.setLocale(code);
            putLangLsCode(code);
        }
    };
    const adjustLanguageSettings =  async (): Promise<void> => {
        const appSetLocale = $i18n.getLocaleCookie();
        if (appSetLocale && getSettings.value?.language && !getSettings.value.language.includes(appSetLocale)) {
            sendLangToApi(appSetLocale);
        }
        initLanguage();
    };

    const sendLangToApi =  async (appSetLocale: string): Promise<void> => {
        if(isClient.value) {
            const language = appSetLocale === LangCodes.FR ? LangApiCodes.FR : LangApiCodes.EN;
            await setSettingsLanguage(language);
        }
    };
    const setLocale = (code: LangCodes): void => {
        const browserLocale = $i18n.getBrowserLocale();
        if (code === LangCodes.SYSTEM && !!browserLocale) {
            switchToSystemLang(browserLocale);
            sendLangToApi(browserLocale);
        } else {
            $i18n.setLocale(code);
            sendLangToApi(code);
        }
        putLangLsCode(code);
    }

    const getCurrLangCode = (): LangCodes => {
        const browserLocale = $i18n.getBrowserLocale();
        const appSetLocale = $i18n.getLocaleCookie();
        
        const lsLangCode = getLangLsCode();
        if (lsLangCode) {
            return lsLangCode;
        } else {
            if (browserLocale === appSetLocale) {
                return LangCodes.SYSTEM;
            } else {
                return appSetLocale as LangCodes;
            }
        }
    };
    return {
        settingsLocales,
        initLanguage,
        adjustLanguageSettings,
        setLocale,
        getLangLsCode,
        getCurrLangCode,
        availabilityLanguages,
        switchToSystemLang
    }
}
